import {VanComponent} from "@utils/vanComponent.ts";
import van, {type State} from "vanjs-core";
import {VanUtil, VanUtil as vu} from "@utils/vanUtil.ts";
import {LoginState} from "@login/loginState.ts";
import {Busy} from "@commons/layout/busy.ts";
import {Net} from "@utils/net.ts";
import type {VerifyResetPasswordCodeResponse} from "pnyx-dto";
import type {ResetPasswordResponse} from "pnyx-dto";
const {div, a, progress, h1, p, form, label, button} = van.tags;

export interface IForgotPasswordChangePasswordParent
{
    onReturnToSignIn(userName: string): void;
    onPasswordChanged(userName: string): void;
}

export type ForgotPasswordPromptCodeOptions =
{
    data: VerifyResetPasswordCodeResponse;
    parent: IForgotPasswordChangePasswordParent;
}

type InternalForm =
{
    userName: string;
    code: string;
    newPassword: State<string>;
    confirmPassword: State<string>;
}

export class ForgotPasswordChangePassword extends VanComponent
{
    #parent: IForgotPasswordChangePasswordParent;
    #form: InternalForm;
    private readonly progress:HTMLElement;
    #showing: State<boolean>;

    constructor(args: ForgotPasswordPromptCodeOptions)
    {
        super();

        this.#parent = args.parent;
        this.#showing = van.state(false);

        this.#form = {
            userName: args.data.userName,
            code: args.data.code,
            newPassword: van.state(''),
            confirmPassword: van.state(''),
        };

        this.progress = Busy.visibility(progress({ "aria-label": "Submitting form" }));
    }

    toDom(): HTMLElement
    {
        return div(this.toDomTags({"class": "login-form-pane", style: 'padding-top: 10px'}),
            p({ class: 'login-kicker' }, 'Account Recovery'),
            h1({ class: 'login-title' }, 'Set Password'),
            p({ class: 'mt-1' }, "Choose a strong password that you haven't used before." ),
            form({ onsubmit: event => this.submit(event) },
                div({class: "login-field mt-1"},
                    label({class: "login-label", for: 'new-password'}, 'New Password'),
                    div({ class: 'login-password-row' },
                        VanUtil.password(VanUtil.turnPasswordManagers({ class: 'form-control', id: 'new-password',
                            tabIndex: 1, required: 'required', autofocus: true,
                            bind: this.#form.newPassword,
                            showing: this.#showing
                        })),
                    ),
                    vu.load(vu.focalOnLoad)
                ),
                div({class: "login-field mt-1"},
                    label({class: "login-label", for: 'confirm-password'}, 'Confirm Password'),
                    div({ class: 'login-password-row' },
                        VanUtil.password(VanUtil.turnPasswordManagers({ class: 'form-control', id: 'confirm-password', 
                            tabIndex: 2, required: 'required',
                            bind: this.#form.confirmPassword,
                            showing: this.#showing
                        })),
                    ),
                ),
                div({ class: 'mt-2'},
                    button({ class: "login-submit", type: 'submit', tabIndex: 3 }, 'Change Password'),
                    div({class: "mt-1 mb-1"}, this.progress),
                ),
                div({ class: "text-center" },
                    a({ class: 'link-plain text-body-secondary', onclick: () => this.#parent.onReturnToSignIn('') }, 'Return to sign-in')
                )
            )
        );
    }

    async submit(event: Event): Promise<void>
    {
        LoginState.instance.alert.hide();
        event.preventDefault();

        let response: ResetPasswordResponse = await Net.post<ResetPasswordResponse>({
            url: '/forgotPassword/resetPassword', data: this.#form
        });
        this.#parent.onPasswordChanged(response.userName);
    }
}